import clsx from 'clsx';
import { QuestionIcon } from '@/components/icons/svg-icons';
import { BetslipService } from '@/services/betslip.service';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useOuterClick } from '@nf/utils-common/hooks';
import { useTranslations } from 'next-intl';
import { memo, useRef, useState } from 'react';
import { betslipStyle } from '@/features/betslip/betslip.style';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';

interface PayoutProps {
	payout: number;
	betType?: string;
	betTeam?: string;
	isEndingGameMultiTicket?: boolean;
	payoutTips?: React.RefObject<HTMLDivElement>;
}

/** 
 * Payout - 收合模式
 * * [galaxy] payout > 0 才顯示
 * * [mars] 固定顯示
 */
export const Payout = memo(function Payout({ payout, betType, betTeam, isEndingGameMultiTicket = false }: PayoutProps) {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const [isTipOpen, setIsTipOpen] = useState(false);
	const payoutTips = useRef<HTMLDivElement>(null);
	useOuterClick(payoutTips, () => {
		setIsTipOpen(false);
	});

	const handleTipStatus = () => {
		setIsTipOpen(!isTipOpen);
	}

	const getPayoutTitle = (type: '' | 'profit' | 'liability'): string => {
		let title;

		switch (type) {
			case 'profit':
				title = t('lbl_Crkt_Profit');
				break;
			case 'liability':
				title = t('lbl_Crkt_Liability');
				break;
			default:
				title = t('lbl_payout');
				break;
		}
		return title;
	}

	const type = betType && betTeam ? BetslipService.getPayoutType(betType, betTeam) : '';
	const title = getPayoutTitle(type);

	return view === ViewEnum.mars || payout ? (
		<div className={clsx('bet-slip__payout', betslipStyle.textInfo)}>
			{title}:
			{
				isEndingGameMultiTicket ? (
					<div
						className="bet-slip__payout-btn"
						onClick={handleTipStatus}
						ref={payoutTips}
					>
						<QuestionIcon />
						<div className="tip tip--top-right" data-open={isTipOpen}>
							<div className="scrollbar scrollbar--tip">
								<div className="tip__content">
									{t('lbl_payoutHint')}
								</div>
							</div>
						</div>
					</div>
				) : (
					<span className={clsx('ticket__payout-amount', betslipStyle.textSecondary)}>{addCommasWithFix(payout, 2)}</span>
				)
			}
		</div >
	) : null;
});

/** Payout - 展開模式的 Multi 區塊用 */
export const TicketPayout = ({ payout, betType, betTeam }: PayoutProps) => {
	const t = useTranslations('General');
	const getPayoutTitle = (type: '' | 'profit' | 'liability'): string => {
		let title;

		switch (type) {
			case 'profit':
				title = t('lbl_Crkt_Profit');
				break;
			case 'liability':
				title = t('lbl_Crkt_Liability');
				break;
			default:
				title = t('lbl_payout');
				break;
		}
		return title;
	}

	const type = betType && betTeam ? BetslipService.getPayoutType(betType, betTeam) : '';
	const title = getPayoutTitle(type);

	return payout ? (
		<div className="ticket__payout">
			{title}:
			<span className="ticket__payout-amount">{addCommasWithFix(payout, 2)}</span>
		</div>
	) : null;
};

/** Payout - 展開模式的 Parlay 區塊用 */
export const BetSlipParlayPayout = ({ payout }: PayoutProps) => {
	const t = useTranslations('General');

	return payout ? (
		<div className="bet-slip-parlay__payout">{t('lbl_payout')}:<span className={betslipStyle.textSecondary}>{addCommasWithFix(payout, 2)}</span></div>
	) : null;
};